import { API_BASE_URL, STORAGE_KEYS } from './config';

// Field error for form validation
export interface FieldError {
	path: string;
	error: string;
}

// Custom error class for API errors
export class ApiError extends Error {
	status: number;
	fieldErrors?: FieldError[];

	constructor(message: string, status: number, fieldErrors?: FieldError[]) {
		super(message);
		this.name = 'ApiError';
		this.status = status;
		this.fieldErrors = fieldErrors;
	}

	// Helper to check if this is a permission denied error
	isPermissionDenied(): boolean {
		return this.status === 403;
	}

	// Helper to check if this is a validation error
	isValidationError(): boolean {
		return this.status === 400;
	}

	// Helper to check if this is a not found error
	isNotFound(): boolean {
		return this.status === 404;
	}

	// Helper to check if this is an unauthorized error
	isUnauthorized(): boolean {
		return this.status === 401;
	}

	// Helper to get field error messages as a single string
	getFieldErrorMessages(): string | null {
		if (!this.fieldErrors || this.fieldErrors.length === 0) return null;
		return this.fieldErrors.map(fe => fe.error).join(', ');
	}
}

// Request options type
interface RequestOptions extends Omit<globalThis.RequestInit, 'body'> {
	body?: unknown;
	/** Skip automatic logout redirect on 401 responses (e.g., for change-password where 401 means wrong current password) */
	skipAuthRedirect?: boolean;
	/**
	 * Never attach the stored bearer token. Public (unauthenticated) endpoints
	 * such as the customer booking pages must not leak a stale portal session —
	 * a provider who is signed in on the same browser would otherwise send their
	 * own token when opening a shared booking link.
	 */
	skipAuth?: boolean;
}

// Get auth token from storage
const getAuthToken = (): string | null => {
	return (
		localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
		sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
	);
};

// Handle 401 unauthorized responses
const handleUnauthorized = () => {
	localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
	localStorage.removeItem(STORAGE_KEYS.USER_DATA);
	sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
	sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);

	if (window.location.pathname !== '/login') {
		window.location.href = '/login';
	}
};

// Main fetch wrapper
async function apiClient<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
	const { body, headers: customHeaders, skipAuthRedirect, skipAuth, ...restOptions } = options;

	const headers: Record<string, string> = {
		'Content-Type': 'application/json',
		Accept: '*/*',
		...(customHeaders as Record<string, string>),
	};

	// Attach auth token if available
	const token = skipAuth ? null : getAuthToken();
	if (token) {
		headers['Authorization'] = `Bearer ${token}`;
	}

	const config: globalThis.RequestInit = {
		...restOptions,
		headers,
	};

	// Add body for non-GET requests
	if (body) {
		config.body = JSON.stringify(body);
	}

	const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

	// Handle 401 Unauthorized - redirect to login unless caller opted out.
	// Unauthenticated callers never had a session to lose, so they are never
	// bounced to the login screen either.
	if (response.status === 401 && !skipAuthRedirect && !skipAuth) {
		handleUnauthorized();
		throw new ApiError('Unauthorized', 401);
	}

	// Parse response
	let data: T;
	const contentType = response.headers.get('content-type');
	if (contentType?.includes('application/json')) {
		data = await response.json();
	} else {
		data = (await response.text()) as T;
	}

	// Handle error responses
	if (!response.ok) {
		const errorData = data as { message?: string; fieldErrors?: FieldError[] };
		const message = errorData?.message || response.statusText;
		const fieldErrors = errorData?.fieldErrors;

		throw new ApiError(message, response.status, fieldErrors);
	}

	return data;
}

// Convenience methods
apiClient.get = <T>(endpoint: string, options?: RequestOptions) =>
	apiClient<T>(endpoint, { ...options, method: 'GET' });

apiClient.post = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
	apiClient<T>(endpoint, { ...options, method: 'POST', body });

apiClient.put = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
	apiClient<T>(endpoint, { ...options, method: 'PUT', body });

apiClient.patch = <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
	apiClient<T>(endpoint, { ...options, method: 'PATCH', body });

apiClient.delete = <T>(endpoint: string, options?: RequestOptions) =>
	apiClient<T>(endpoint, { ...options, method: 'DELETE' });

export default apiClient;
